import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileText, ReceiptText, Printer } from "lucide-react";
import CoutureLayout from "./CoutureLayout";
import { api, formatPrice, formatApiError } from "../../lib/api";
import { useI18n } from "../../i18n/I18nContext";

const STAGE_KEYS = ["commande", "mesures", "coupe", "couture", "finitions", "pret", "livre"];
const STATUS_KEYS = ["en_cours", "pret", "livre", "annule"];

export default function CoutureOrderDetail() {
    const { id } = useParams();
    const { t } = useI18n();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [docs, setDocs] = useState([]);
    const [fields, setFields] = useState([]);

    const load = () => {
        api.get(`/couture/orders/${id}`).then((r) => setOrder(r.data)).catch(() => navigate("/couturier/commandes"));
        api.get(`/couture/orders/${id}/documents`).then((r) => setDocs(r.data)).catch(() => {});
    };

    useEffect(() => {
        api.get("/couture/measurement-fields").then((r) => setFields(r.data)).catch(() => {});
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const patch = async (payload) => {
        try {
            const { data } = await api.put(`/couture/orders/${id}`, payload);
            setOrder(data);
            toast.success(t("couture.saved"));
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const createDoc = async (docType) => {
        try {
            const { data } = await api.post(`/couture/orders/${id}/documents`, null, { params: { doc_type: docType } });
            setDocs((d) => [data, ...d]);
            toast.success(t("couture.doc_created"));
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    if (!order) {
        return (
            <CoutureLayout>
                <div className="p-10 text-gray-400">{t("admin.loading")}</div>
            </CoutureLayout>
        );
    }

    const measureLabel = (key) => fields.find((f) => f.key === key)?.label_fr || key;

    return (
        <CoutureLayout>
            <div className="p-6 md:p-10 max-w-5xl" data-testid="couture-order-detail">
                <Link to="/couturier/commandes" className="inline-flex items-center gap-2 text-xs text-gray-400 hover:text-brand-gold mb-6">
                    <ArrowLeft className="w-4 h-4" /> {t("couture.orders")}
                </Link>

                <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
                    <div>
                        <h1 className="font-display text-3xl text-brand-navy">{order.client_name}</h1>
                        <div className="font-mono text-sm text-brand-gold-deep mt-1">{order.tracking_code}</div>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => createDoc("devis")}
                            data-testid="couture-doc-devis"
                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:border-brand-gold text-brand-navy text-[0.68rem] tracking-[0.18em] uppercase font-semibold transition-colors"
                        >
                            <FileText className="w-4 h-4" /> {t("couture.make_devis")}
                        </button>
                        <button
                            onClick={() => createDoc("facture")}
                            data-testid="couture-doc-facture"
                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-gold hover:bg-brand-gold-deep text-white text-[0.68rem] tracking-[0.18em] uppercase font-semibold transition-colors"
                        >
                            <ReceiptText className="w-4 h-4" /> {t("couture.make_facture")}
                        </button>
                    </div>
                </div>

                {/* Stage stepper */}
                <section className="bg-white border border-gray-100 p-6 mb-6" data-testid="couture-stage-stepper">
                    <h2 className="text-[0.7rem] tracking-[0.22em] uppercase font-bold text-brand-navy mb-5">{t("couture.stage")}</h2>
                    <div className="flex flex-wrap gap-2">
                        {STAGE_KEYS.map((s) => (
                            <button
                                key={s}
                                onClick={() => patch({ stage: s })}
                                data-testid={`couture-stage-${s}`}
                                className={`px-4 py-2 text-[0.66rem] tracking-[0.14em] uppercase font-semibold transition-colors ${
                                    order.stage === s ? "bg-brand-gold text-white" : "bg-brand-paper border border-gray-200 text-gray-500 hover:border-brand-gold"
                                }`}
                            >
                                {t(`couture.stage_${s}`)}
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-4 items-center">
                        <span className="text-[0.62rem] tracking-[0.18em] uppercase text-gray-400 mr-2">{t("couture.status")}:</span>
                        {STATUS_KEYS.map((s) => (
                            <button
                                key={s}
                                onClick={() => patch({ status: s })}
                                data-testid={`couture-status-${s}`}
                                className={`px-3 py-1.5 text-[0.62rem] tracking-[0.14em] uppercase font-semibold transition-colors ${
                                    order.status === s ? "bg-brand-navy text-brand-gold" : "bg-white border border-gray-200 text-gray-500 hover:border-brand-gold"
                                }`}
                            >
                                {t(`couture.status_${s}`)}
                            </button>
                        ))}
                    </div>
                </section>

                <div className="grid lg:grid-cols-2 gap-6 mb-6">
                    {/* Details */}
                    <section className="bg-white border border-gray-100 p-6">
                        <h2 className="text-[0.7rem] tracking-[0.22em] uppercase font-bold text-brand-navy mb-5">{t("couture.section_client")}</h2>
                        <dl className="text-sm space-y-3">
                            <div className="flex justify-between gap-4">
                                <dt className="text-gray-400">{t("couture.client_name")}</dt>
                                <dd className="font-semibold text-brand-navy text-right">{order.client_name}</dd>
                            </div>
                            <div className="flex justify-between gap-4">
                                <dt className="text-gray-400">{t("couture.client_phone")}</dt>
                                <dd className="text-brand-navy text-right">{order.client_phone || "—"}</dd>
                            </div>
                            <div className="flex justify-between gap-4">
                                <dt className="text-gray-400">{t("couture.category")}</dt>
                                <dd className="text-brand-navy text-right">
                                    {order.category_name || "—"}{order.subcategory_name ? ` / ${order.subcategory_name}` : ""}
                                </dd>
                            </div>
                            <div className="flex justify-between gap-4">
                                <dt className="text-gray-400">{t("couture.due_date")}</dt>
                                <dd className="text-brand-navy text-right">{order.due_date || "—"}</dd>
                            </div>
                        </dl>
                        {order.description && (
                            <p className="mt-4 text-sm text-gray-600 border-t border-gray-100 pt-4">{order.description}</p>
                        )}
                    </section>

                    {/* Measurements + payment */}
                    <section className="bg-white border border-gray-100 p-6">
                        <h2 className="text-[0.7rem] tracking-[0.22em] uppercase font-bold text-brand-navy mb-5">{t("couture.section_measurements")}</h2>
                        {Object.keys(order.measurements || {}).length === 0 ? (
                            <div className="text-sm text-gray-400">—</div>
                        ) : (
                            <dl className="grid grid-cols-2 gap-3 text-sm">
                                {Object.entries(order.measurements).map(([k, v]) => (
                                    <div key={k} className="bg-brand-paper px-3 py-2">
                                        <dt className="text-[0.6rem] tracking-[0.14em] uppercase text-gray-400">{measureLabel(k)}</dt>
                                        <dd className="font-semibold text-brand-navy">{v}</dd>
                                    </div>
                                ))}
                            </dl>
                        )}
                        <h2 className="text-[0.7rem] tracking-[0.22em] uppercase font-bold text-brand-navy mt-6 mb-4">{t("couture.section_payment")}</h2>
                        <dl className="text-sm space-y-2">
                            <div className="flex justify-between">
                                <dt className="text-gray-400">{t("couture.price")}</dt>
                                <dd className="font-semibold text-brand-navy">{formatPrice(order.price_fcfa, "FCFA")}{order.price_eur != null ? ` · ${formatPrice(order.price_eur, "EUR")}` : ""}</dd>
                            </div>
                            {order.cost_fcfa != null && (
                                <div className="flex justify-between">
                                    <dt className="text-gray-400">{t("couture.cost")}</dt>
                                    <dd className="font-semibold text-brand-navy">{formatPrice(order.cost_fcfa, "FCFA")}</dd>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <dt className="text-gray-400">{t("couture.deposit")}</dt>
                                <dd className="font-semibold text-emerald-600">{formatPrice(order.deposit_fcfa, "FCFA")}</dd>
                            </div>
                            <div className="flex justify-between">
                                <dt className="text-gray-400">{t("couture.balance")}</dt>
                                <dd className="font-semibold text-brand-gold-deep">{formatPrice(order.balance_fcfa, "FCFA")}</dd>
                            </div>
                            {order.profit_fcfa != null && (
                                <div className="flex justify-between border-t border-gray-100 pt-2 mt-2" data-testid="couture-order-profit">
                                    <dt className="text-gray-400">{t("couture.profit")}</dt>
                                    <dd className={`font-semibold ${order.profit_fcfa >= 0 ? "text-emerald-600" : "text-red-600"}`}>{formatPrice(order.profit_fcfa, "FCFA")}</dd>
                                </div>
                            )}
                        </dl>
                    </section>
                </div>

                {/* Documents */}
                <section className="bg-white border border-gray-100 p-6" data-testid="couture-documents">
                    <h2 className="text-[0.7rem] tracking-[0.22em] uppercase font-bold text-brand-navy mb-5">{t("couture.documents")}</h2>
                    {docs.length === 0 ? (
                        <div className="text-sm text-gray-400" data-testid="couture-docs-empty">{t("couture.no_documents")}</div>
                    ) : (
                        <div className="space-y-2">
                            {docs.map((d) => (
                                <div key={d.id} className="flex items-center justify-between bg-brand-paper px-4 py-3" data-testid={`couture-doc-${d.doc_number}`}>
                                    <div className="flex items-center gap-3">
                                        {d.doc_type === "facture" ? <ReceiptText className="w-4 h-4 text-brand-gold" /> : <FileText className="w-4 h-4 text-brand-gold" />}
                                        <div>
                                            <div className="font-mono text-sm font-semibold text-brand-navy">{d.doc_number}</div>
                                            <div className="text-xs text-gray-400">{t(`couture.doc_${d.doc_type}`)} · {new Date(d.created_at).toLocaleDateString("fr-FR")}</div>
                                        </div>
                                    </div>
                                    <Link
                                        to={`/couturier/documents/${d.id}`}
                                        target="_blank"
                                        data-testid={`couture-doc-open-${d.doc_number}`}
                                        className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 hover:border-brand-gold text-brand-navy text-[0.62rem] tracking-[0.16em] uppercase font-semibold transition-colors"
                                    >
                                        <Printer className="w-3.5 h-3.5" /> {t("couture.print")}
                                    </Link>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </div>
        </CoutureLayout>
    );
}
